import { Search, X } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { cn } from '~/utils/cn';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Extra classes applied to the underlying `Input`, e.g. `h-8 text-sm` for a smaller box. */
  inputClassName?: string;
  /** When true, `onChange` fires only on Enter or Clear — keystrokes update a
   *  local draft. Use for server-side searches where each keystroke would be
   *  expensive. Defaults to false (legacy as-you-type behaviour). */
  commitOnEnter?: boolean;
  disabled?: boolean;
  /** Forwarded to the underlying `Input` so a `<label htmlFor>` can name the
   *  field. A placeholder is not an accessible name, and callers that render a
   *  visible or sr-only label need something to point at. */
  id?: string;
  /** Forwarded to the underlying `Input` — the longest value the field accepts. */
  maxLength?: number;
  /** Forwarded to the underlying `Input` — e.g. a hint that the search runs on Enter. */
  ariaDescribedBy?: string;
  /** `data-testid` root (wrapper); derives `${testId}-input` and `${testId}-clear-button`. */
  testId?: string;
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  className,
  inputClassName,
  commitOnEnter = false,
  disabled = false,
  id,
  maxLength,
  ariaDescribedBy,
  testId = 'search-input',
}: SearchInputProps) {
  const { t } = useTranslation('common');
  // Local draft used only when commitOnEnter is on. Re-syncs to the parent's
  // committed `value` whenever the parent resets externally — the render-time
  // compare pattern avoids the cascading-render trap that an effect would have.
  const [draft, setDraft] = useState(value);
  const [lastValue, setLastValue] = useState(value);
  if (commitOnEnter && value !== lastValue) {
    setLastValue(value);
    setDraft(value);
  }

  const inputValue = commitOnEnter ? draft : value;

  function handleChange(next: string) {
    if (commitOnEnter) {
      setDraft(next);
    } else {
      onChange(next);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (commitOnEnter && e.key === 'Enter') {
      e.preventDefault();
      onChange(draft.trim());
    }
  }

  function handleClear() {
    if (commitOnEnter) setDraft('');
    onChange('');
  }

  return (
    <div className={cn('relative', className)} data-testid={testId}>
      <Search
        aria-hidden="true"
        className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
      />
      <Input
        // Spread rather than `id={id}`: `exactOptionalPropertyTypes` rejects an
        // explicit `undefined` for an optional prop.
        {...(id !== undefined ? { id } : {})}
        {...(maxLength !== undefined ? { maxLength } : {})}
        {...(ariaDescribedBy !== undefined ? { 'aria-describedby': ariaDescribedBy } : {})}
        data-testid={`${testId}-input`}
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          handleChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          'pl-9',
          inputValue && 'pr-8',
          disabled && 'cursor-not-allowed',
          inputClassName,
        )}
      />
      {inputValue && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-1/2 right-1 h-6 w-6 -translate-y-1/2 cursor-pointer"
          data-testid={`${testId}-clear-button`}
          onClick={handleClear}
        >
          <X className="h-3.5 w-3.5" />
          <span className="sr-only">
            {t('searchInput.clear', { defaultValue: 'Clear search' })}
          </span>
        </Button>
      )}
    </div>
  );
}
